import React, { useState } from "react";
import { audioSupported, playChord } from "../../lib/keyboardPlan";
import {
  stepPattern,
  spellScale,
  scaleMidis,
  scaleMidisDown,
  scaleTriad,
  playMidiSeq,
  keyLabel,
} from "../../lib/numberSenseV2";
import { Icon, Keys } from "../numberSenseUi";

const CAN_PLAY = audioSupported();

/* ============================================================
   Number Sense v2 — the "learn" intro for one scale type. Same
   swipeable-slides shape (and CSS) as v1's TeachScreen, but the
   slide extras render formula-derived content:
     pattern : the W/H step chips
     scale   : { key } the scale spelled + played in a key
     compare : { keys } the same pattern in two tonics
     chords  : { key } the triads each degree grows
   ============================================================ */

const HearScale = ({ type, tonic, down = false, label = "hear it" }) => {
  const [playing, setPlaying] = useState(false);
  const run = () => {
    const midis = down ? scaleMidisDown(type, tonic) : scaleMidis(type, tonic);
    const ms = playMidiSeq(midis, 300);
    setPlaying(true);
    window.setTimeout(() => setPlaying(false), ms);
  };
  return (
    <button type="button" className={`ns-play ${playing ? "on" : ""}`} onClick={run}>
      <Icon name="play" />
      <span>{label}</span>
    </button>
  );
};

// The step recipe as chips: W · W · H …
const PatternView = ({ type }) => (
  <div className="teach-ex">
    <div className="v2-pattern" aria-label={`step pattern: ${stepPattern(type).join(" ")}`}>
      {stepPattern(type).map((s, i) => (
        <span className={`v2-step ${s === "H" ? "half" : s === "W+H" ? "leap" : ""}`} key={i}>
          {s}
        </span>
      ))}
    </div>
    <div className="teach-ex-foot">
      <span className="teach-ex-key">
        W = whole step · H = half step{stepPattern(type).includes("W+H") ? " · W+H = a step and a half" : ""}
      </span>
      {CAN_PLAY && <HearScale type={type} tonic={type.refKey} label="hear the climb" />}
    </div>
  </div>
);

// One key's scale as numbered chips (degree over note), like v1's ScaleView.
// `down` renders the walk from the top tonic — 1 · 7 · 6 … · 1 — matching
// what the descending audio plays.
const ScaleRow = ({ type, tonic, down = false }) => {
  const notes = spellScale(type, tonic);
  const labels = type.degreeLabels;
  const chips = down
    ? [
        [labels[0], notes[0]],
        ...notes
          .slice(1)
          .map((n, i) => [labels[i + 1], n])
          .reverse(),
        [labels[0], notes[0]],
      ]
    : notes.map((n, i) => [labels[i], n]);
  return (
    <div className="teach-chips">
      {chips.map(([deg, n], i) => (
        <span className="teach-chip" key={`${deg}-${n}-${i}`}>
          <b className="mono">{deg}</b>
          <span>{n}</span>
        </span>
      ))}
    </div>
  );
};

const ScaleView = ({ type, tonic, down = false }) => (
  <div className="teach-ex">
    <ScaleRow type={type} tonic={tonic} down={down} />
    <div className="teach-ex-foot">
      <span className="teach-ex-key">
        {keyLabel(type, tonic)} scale{down ? ", coming down" : ""}
      </span>
      {CAN_PLAY && (
        <HearScale
          type={type}
          tonic={tonic}
          down={down}
          label={down ? "hear it fall" : "hear the scale"}
        />
      )}
    </div>
  </div>
);

const CompareView = ({ type, keys }) => (
  <div className="v2-compare">
    {keys.map((k) => (
      <div className="teach-ex" key={k}>
        <ScaleRow type={type} tonic={k} />
        <div className="teach-ex-foot">
          <span className="teach-ex-key">{keyLabel(type, k)}</span>
          {CAN_PLAY && <HearScale type={type} tonic={k} />}
        </div>
      </div>
    ))}
  </div>
);

// The diatonic triads a scale grows, tappable to hear each.
const ChordsView = ({ type, tonic }) => (
  <div className="teach-ex">
    <div className="teach-chips">
      {type.semis.map((s, i) => {
        const triad = scaleTriad(type, tonic, i);
        return (
          <button
            type="button"
            className="teach-chip v2-chord-chip"
            key={triad.symbol + i}
            onClick={() => CAN_PLAY && playChord(triad.notes)}
          >
            <b className="mono">{triad.roman || type.degreeLabels[i]}</b>
            <span>{triad.symbol}</span>
            <span className="teach-chip-notes mono">{triad.notes.join(" ")}</span>
          </button>
        );
      })}
    </div>
    <div className="teach-ex-foot">
      <span className="teach-ex-key">in {keyLabel(type, tonic)} — tap one to hear it</span>
    </div>
  </div>
);

const TeachScreen = ({ type, slides = [], onDone, onClose }) => {
  const [i, setI] = useState(0);
  const slide = slides[i] || { title: "", body: "" };
  const last = i >= slides.length - 1;

  return (
    <div className="ns-root">
      <div className="ns-app">
        <div className="subbar">
          <button className="iconbtn" onClick={onClose} aria-label="close">
            <Icon name="back" />
          </button>
          <div className="subbar-title">{type.title}</div>
        </div>
        <div className="scroll">
          <div className="teach-card">
            <div className="teach-kicker">
              <Icon name="book" />
              <span>learn</span>
            </div>
            <h2 className="teach-title">{slide.title}</h2>
            <p className="teach-body">{slide.body}</p>
            {slide.pattern && <PatternView type={type} />}
            {slide.scale && (
              <ScaleView
                type={type}
                tonic={slide.scale.key}
                down={Boolean(slide.scale.down)}
              />
            )}
            {slide.compare && <CompareView type={type} keys={slide.compare.keys} />}
            {slide.chords && <ChordsView type={type} tonic={slide.chords.key} />}
          </div>

          {slides.length > 1 && (
            <div className="teach-dots" aria-hidden="true">
              {slides.map((s, idx) => (
                <i key={s.title} className={idx === i ? "on" : ""} />
              ))}
            </div>
          )}

          <div className="teach-nav">
            {i > 0 ? (
              <button className="btn ghost" onClick={() => setI(i - 1)}>
                back
              </button>
            ) : (
              <span />
            )}
            {last ? (
              <button className="btn" onClick={onDone}>
                pick a key
              </button>
            ) : (
              <button className="btn" onClick={() => setI(i + 1)}>
                next
              </button>
            )}
          </div>

          <div className="footnote">
            <Keys />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeachScreen;
